import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import { CidCvnRetailerInfo } from '@packages/models/api';
import { DetailViewAside } from '../../components/detail-view';
import { SkeletonLoader } from '@web/components/skeleton-loader';
import { Tab, Tabs } from 'react-bootstrap';
import { Icon, Typography } from '@packages/ui/shared';
import Avatar from '@web/components/avatar';
import colors from '@packages/core/styles/colors';

enum TabKey {
    DETAILS = 'DETAILS',
}

const useStyles = createUseStyles({
    asideContainer: {
        padding: 0,
        height: '100%',
    },
    iconWrapper: {
        alignItems: 'center',
        backgroundColor: colors.grayTwo,
        borderRadius: '50%',
        display: 'flex',
        height: '160px',
        justifyContent: 'center',
        width: '160px',
    },
    notAvailableContainer: {
        lineHeight: '32px',
        marginTop: '20px',
        textAlign: 'center',
    },
    tabs: {
        padding: '14px 24px 0px 24px',
    },
});

function RetailerInfoNotAvailable({ classes }: { classes: any }) {
    return (
        <div className="d-flex flex-column align-items-center">
            <div className={classes.iconWrapper}>
                <Icon name="car-lift" size={60} color="grayFive" />
            </div>
            <div className={classes.notAvailableContainer}>
                <Typography variant="h3" color="textDarkSecondary">
                    Retailer information
                    <br />
                    is not available
                </Typography>
            </div>
        </div>
    );
}

function getRetailerNameAndCode(retailer: CidCvnRetailerInfo | undefined): string {
    if (!retailer?.name) {
        return 'RETAILER UNAVAILABLE';
    }
    if (retailer.code) {
        return `${retailer.name} (#${retailer.code})`;
    }
    return retailer.name;
}

function RetailerInfo({ retailer, isLoading }: { retailer?: CidCvnRetailerInfo; isLoading: boolean }) {
    const retailerNameAndCode = getRetailerNameAndCode(retailer);

    const retailerLocation = [
        retailer?.region && `Region ${retailer.region}`,
        retailer?.zone && `Zone ${retailer.zone}`,
        retailer?.district && `District ${retailer.district}`,
    ]
        .filter(Boolean)
        .join(', ');

    return (
        <>
            <Typography variant="caption4">Retailer</Typography>
            <div className="mt-3 d-flex flex-column">
                {isLoading ? (
                    <div>
                        <SkeletonLoader type="text" height={18} className="mb-1" />
                        <SkeletonLoader type="text" height={18} width={210} className="mb-1" />
                    </div>
                ) : (
                    <>
                        <Typography color="blueOne">{retailerNameAndCode}</Typography>
                        <div className="mt-1">
                            <Typography>{retailerLocation}</Typography>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

function CreatedByInfo({
    avatar,
    createdBy,
    createdDate,
    isLoading,
}: {
    avatar?: { firstName?: string; lastName?: string; imageUrl?: string };
    createdBy?: string;
    createdDate: string;
    isLoading: boolean;
}) {
    const [firstName = '', lastName = ''] = (createdBy || '').split(' ');
    return (
        <div className="pt-6">
            <Typography variant="caption4">Created</Typography>
            <div className="d-flex flex-row align-items-center mt-3">
                <Avatar firstName={firstName} lastName={lastName} imageUrl={avatar?.imageUrl} size={32} />
                <div className="d-flex flex-column ml-4">
                    {isLoading ? (
                        <SkeletonLoader type="text" height={24} width={160} className="mb-0" />
                    ) : (
                        <>
                            <Typography color="blueOne">{createdBy}</Typography>
                            <Typography variant="caption">{createdDate}</Typography>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

interface CidCvnRightAsideProps {
    createdBy?: string;
    createdDate: string;
    isLoading: boolean;
    retailer?: CidCvnRetailerInfo;
    showDetails: boolean;
}

export function CidCvnRightAside({ createdBy, createdDate, isLoading, retailer, showDetails }: CidCvnRightAsideProps) {
    const [tab, setTab] = useState<string>(TabKey.DETAILS);
    const classes = useStyles();
    return (
        <DetailViewAside classNames={classes.asideContainer}>
            <Tabs
                activeKey={tab}
                className={classes.tabs}
                id="cid-cvn-tabs"
                mountOnEnter
                unmountOnExit
                onSelect={(tab) => {
                    if (tab) {
                        setTab(tab);
                    }
                }}
            >
                <Tab
                    className="pt-0"
                    eventKey={TabKey.DETAILS}
                    title={
                        <Typography variant="label" color="blueOne">
                            Details
                        </Typography>
                    }
                >
                    {showDetails || isLoading ? (
                        <div className="p-6">
                            <RetailerInfo retailer={retailer} isLoading={isLoading} />
                            <CreatedByInfo createdBy={createdBy} createdDate={createdDate} isLoading={isLoading} />
                        </div>
                    ) : (
                        <div className="p-6">
                            <RetailerInfoNotAvailable classes={classes} />
                        </div>
                    )}
                </Tab>
            </Tabs>
        </DetailViewAside>
    );
}
